/** Typed analytics endpoints — /v1/analytics/* */
import { api } from "@/lib/api";

export interface AnalyticsPeriod {
  from: string;
  to: string;
  days: number;
}

export interface AnalyticsOverview {
  period: AnalyticsPeriod;
  jobs: {
    created: number;
    created_prev: number;
    created_pct: number | null;
    completed: number;
    completed_prev: number;
    completed_pct: number | null;
    cancelled: number;
    cancellation_rate: number;
    avg_resolution_hours: number | null;
    by_status: Record<string, number>;
  };
  revenue: {
    billed: string;
    billed_prev: string;
    billed_pct: number | null;
    payout: string;
    margin: string;
    avg_ticket: string;
  };
  people: {
    new_clients: number;
    new_nerds: number;
    active_nerds: number;
    pending_nerds: number;
    active_clients: number;
    total_clients: number;
  };
  invoices: {
    paid_in_period: number;
    pending: number;
  };
  sla: {
    on_time: number;
    late: number;
    compliance_pct: number | null;
  };
}

export type TsMetric = "jobs" | "revenue" | "completed" | "cancelled";
export type TsGranularity = "day" | "week" | "month";

export interface TsPoint {
  date: string;
  value: number;
}

export interface ByCategoryRow {
  id: string;
  category: string;
  jobs: number;
  revenue: string;
}

export interface ByCidadeRow {
  id: string;
  name: string;
  uf: string;
  lat: number | null;
  lng: number | null;
  jobs: number;
}

export interface TopClientRow {
  id: string;
  trade_name: string;
  cnpj: string;
  jobs: number;
  revenue: string;
}

export interface TopNerdRow {
  id: string;
  legal_name: string | null;
  rank_score: number;
  jobs: number;
  payout: string;
}

export interface FunnelStage {
  stage: string;
  count: number;
}

export interface SignupsTimeseries {
  clients: TsPoint[];
  nerds: TsPoint[];
  users: TsPoint[];
}

export interface AnalyticsParams {
  from?: string;
  to?: string;
}

function buildParams(p: Record<string, unknown> = {}): Record<string, unknown> {
  const { from, to, ...rest } = p;
  const out: Record<string, unknown> = { ...rest };
  if (from) out.from = from;
  if (to) out.to = to;
  return out;
}

export const analyticsApi = {
  overview: async (p?: AnalyticsParams): Promise<AnalyticsOverview> => {
    const { data } = await api.get<AnalyticsOverview>("/v1/analytics/overview", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  timeseries: async (
    p: AnalyticsParams & { metric?: TsMetric; granularity?: TsGranularity } = {},
  ): Promise<TsPoint[]> => {
    const { data } = await api.get<TsPoint[]>("/v1/analytics/timeseries", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  byCategory: async (p?: AnalyticsParams): Promise<ByCategoryRow[]> => {
    const { data } = await api.get<ByCategoryRow[]>("/v1/analytics/by-category", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  byCidade: async (limit = 20): Promise<ByCidadeRow[]> => {
    const { data } = await api.get<ByCidadeRow[]>("/v1/analytics/by-cidade", { params: { limit } });
    return data;
  },
  topClients: async (p: AnalyticsParams & { limit?: number } = {}): Promise<TopClientRow[]> => {
    const { data } = await api.get<TopClientRow[]>("/v1/analytics/top-clients", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  topNerds: async (p: AnalyticsParams & { limit?: number } = {}): Promise<TopNerdRow[]> => {
    const { data } = await api.get<TopNerdRow[]>("/v1/analytics/top-nerds", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  funnel: async (p?: AnalyticsParams): Promise<FunnelStage[]> => {
    const { data } = await api.get<FunnelStage[]>("/v1/analytics/funnel", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
  signupsTimeseries: async (months = 12): Promise<SignupsTimeseries> => {
    const { data } = await api.get<SignupsTimeseries>("/v1/analytics/signups-timeseries", { params: { months } });
    return data;
  },
  hourlyHeatmap: async (p?: AnalyticsParams): Promise<number[][]> => {
    const { data } = await api.get<number[][]>("/v1/analytics/hourly-heatmap", { params: buildParams(p as Record<string, unknown>)});
    return data;
  },
};
